/**
 * Activities Feature Hooks
 * React Query hooks for Activities data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import {
  getAttivita,
  getAttivitaById,
  getAttivitaByStato,
  getAttivitaInScadenza,
  createAttivita,
  updateAttivita,
  deleteAttivita,
  getAttivitaStats,
} from '@/api/queries';
import type { Attivita, AttivitaInsert, AttivitaUpdate } from '@/types';

// Query keys
export const activitiesKeys = {
  all: ['activities'] as const,
  lists: () => [...activitiesKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...activitiesKeys.lists(), filters] as const,
  details: () => [...activitiesKeys.all, 'detail'] as const,
  detail: (id: string) => [...activitiesKeys.details(), id] as const,
  stats: () => [...activitiesKeys.all, 'stats'] as const,
  scadenza: (giorni: number) => [...activitiesKeys.all, 'scadenza', giorni] as const,
};

/**
 * Hook to fetch all activities
 */
export function useActivities(filters?: Record<string, unknown>) {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: activitiesKeys.list(filters || {}),
    queryFn: async () => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { data, error } = await getAttivita({
        userId: currentUser.id,
        filters: filters || {},
        order: { column: 'data_scadenza', ascending: true },
      });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentUser?.id,
  });
}

/**
 * Hook to fetch activities by status
 */
export function useActivitiesByStato(stato: Attivita['stato']) {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: [...activitiesKeys.lists(), 'stato', stato],
    queryFn: async () => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { data, error } = await getAttivitaByStato(stato, {
        userId: currentUser.id,
      });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentUser?.id,
  });
}

/**
 * Hook to fetch a single activity by ID
 */
export function useActivity(id: string) {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: activitiesKeys.detail(id),
    queryFn: async () => {
      if (!currentUser?.id || !id) throw new Error('Missing user or activity ID');
      const { data, error } = await getAttivitaById(id, currentUser.id);
      if (error) throw error;
      return data;
    },
    enabled: !!currentUser?.id && !!id,
  });
}

/**
 * Hook to fetch activities in scadenza
 */
export function useActivitiesInScadenza(giorni: number = 7) {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: activitiesKeys.scadenza(giorni),
    queryFn: async () => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { data, error } = await getAttivitaInScadenza(giorni);
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentUser?.id,
  });
}

/**
 * Hook to fetch activity stats
 */
export function useActivityStats() {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: activitiesKeys.stats(),
    queryFn: async () => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { stats, error } = await getAttivitaStats(currentUser.id);
      if (error) throw error;
      return stats || { daFare: 0, inCorso: 0, completate: 0, annullate: 0, totali: 0 };
    },
    enabled: !!currentUser?.id,
  });
}

/**
 * Hook to create a new activity
 */
export function useCreateActivity() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  return useMutation({
    mutationFn: async (activity: AttivitaInsert) => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { data, error } = await createAttivita(activity, currentUser.id);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitiesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: activitiesKeys.stats() });
    },
  });
}

/**
 * Hook to update an activity
 */
export function useUpdateActivity() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: AttivitaUpdate }) => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { data, error } = await updateAttivita(id, updates, currentUser.id);
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: activitiesKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: activitiesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: activitiesKeys.stats() });
    },
  });
}

/**
 * Hook to delete an activity
 */
export function useDeleteActivity() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  return useMutation({
    mutationFn: async (id: string) => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { error } = await deleteAttivita(id, currentUser.id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitiesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: activitiesKeys.stats() });
    },
  });
}
